import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import Grid from "@/lib/organisms/Grid";
import { CustomCellRendererProps } from "ag-grid-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { EditCleaningRateDialog } from "@/lib/shadcn/components/edit-cleaning-rate-dialog";
import { MissingCleaningRatesBanner } from "@/lib/shadcn/components/missing-cleaning-rates-banner";
import { dateToString } from "@/utils/periods";
import { formatDateToISODate } from "@/utils/dates";
import { utcDateFilterParams } from "@/utils/grid";
import {
    CleaningRate,
    useActiveApartmentEntities,
    useActiveApartments,
    useAddCleaningRateMutation,
    useGetCleaningRateReadinessQuery,
    useRemoveCleaningRateMutation,
    useUpdateCleaningRateMutation,
} from "@/store/slices/apartments";
import { apiUrl, fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { errorPopup, successPopup } from "@/utils/alerts";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";

interface CleaningRateActionsRendererProps extends CustomCellRendererProps {
    onEdit: (rate: CleaningRate) => void;
    onDelete: (rate: CleaningRate) => void;
}

export const CleaningRateActionsRenderer: React.FC<CleaningRateActionsRendererProps> = (
    params: CleaningRateActionsRendererProps,
) => {
    return (
        <div className="flex gap-3">
            <button
                type="button"
                aria-label="Edit cleaning rate"
                onClick={() => params.onEdit(params.data)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit" }}
                className={"text-info-emphasis"}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
                type="button"
                aria-label="Delete cleaning rate"
                onClick={() => params.onDelete(params.data)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit" }}
                className={"text-danger"}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </div>
    );
};

const toNumber = (value: number | string): number => (typeof value === "number" ? value : parseFloat(value) || 0);

/**
 * Builds the cleaning-rates grid columns. Extracted (rather than inlined in the
 * component) so tests can assert against the *source* column config — notably
 * that `effectiveFrom` pairs `agDateColumnFilter` with `cellDataType: "date"`
 * AND a `valueGetter` that hands AG Grid a real `Date` (the wire value is an ISO
 * string). Asserting on the grid-resolved def would be a false-green, since AG
 * Grid infers a type from row values at runtime.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildCleaningRateColumnDefinitions(deps: {
    apartmentEntities: Record<number, { name?: string } | undefined>;
    onEdit: (rate: CleaningRate) => void;
    onDelete: (rate: CleaningRate) => void;
}): ColDef[] {
    const { apartmentEntities, onEdit, onDelete } = deps;
    return [
        {
            field: "apartmentId",
            headerName: "Apartment",
            valueGetter: (params) => apartmentEntities[params.data.apartmentId]?.name,
            width: 300,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "effectiveFrom",
            headerName: "Effective From",
            // `effectiveFrom` arrives as an ISO date *string*. `agDateColumnFilter`
            // needs a real `Date` value paired with `cellDataType: "date"` (a text
            // type makes the filter compare against the formatted label and return
            // no rows), so convert at the value boundary — mirroring how
            // apartments.tsx / payout-ratios-page.tsx feed their date columns.
            valueGetter: (params) => (params.data?.effectiveFrom ? new Date(params.data.effectiveFrom) : null),
            valueFormatter: (params) => (params.value ? dateToString(new Date(params.value)) : ""),
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 200,
        },
        {
            field: "cleaningFee",
            headerName: "Cleaning Fee",
            valueFormatter: (params) => `€${toNumber(params.value).toFixed(2)}`,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 180,
        },
        {
            field: "cleaningCost",
            headerName: "Cleaning Cost",
            valueFormatter: (params) => `€${toNumber(params.value).toFixed(2)}`,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 180,
        },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: CleaningRateActionsRenderer,
            cellRendererParams: {
                onEdit: (rate: CleaningRate) => onEdit(rate),
                onDelete: (rate: CleaningRate) => onDelete(rate),
            },
            filter: false,
            flex: 1,
        },
    ];
}

const CleaningRatesPage: React.FC = () => {
    const apartmentEntities = useActiveApartmentEntities();
    const activeApartments = useActiveApartments();
    const [rates, setRates] = useState<CleaningRate[]>([]);
    const [rateToEdit, setRateToEdit] = useState<CleaningRate | undefined>(undefined);
    // Set when adding a first rate for an apartment that has none (so there is no
    // `rateToEdit` row to carry the apartment id). Mutually exclusive with `rateToEdit`.
    const [addApartmentId, setAddApartmentId] = useState<number | undefined>(undefined);
    const [showEditModal, setShowEditModal] = useState(false);
    const gridName = "Latest Cleaning Rates";

    const { data: readinessIds = [] } = useGetCleaningRateReadinessQuery();
    const [addCleaningRate] = useAddCleaningRateMutation();
    const [updateCleaningRate] = useUpdateCleaningRateMutation();
    const [removeCleaningRate] = useRemoveCleaningRateMutation();

    const { predicates } = useApartmentFilters();

    // Bumping this token re-runs the load effect after a mutation. Mutations
    // also `invalidatesTags(["Apartments"])`, but the grid is fed from a local
    // raw-fetch flattening (there is no "all cleaning rates" endpoint — only a
    // per-apartment list), so we re-fetch explicitly.
    const [reloadToken, setReloadToken] = useState(0);
    const reloadRates = useCallback(() => setReloadToken((token) => token + 1), []);

    /**
     * There is no "all cleaning rates" endpoint — only a per-apartment list — so
     * we fetch each active apartment's rates and flatten them into the grid.
     * setState happens only inside the async `.then`, never synchronously in the
     * effect body. Mirrors `payout-ratios-page.tsx`'s effect-driven fetch.
     */
    useEffect(() => {
        Promise.all(
            activeApartments.map((apartment) =>
                fetch(`${apiUrl}${getApiBasePath()}/admin/apartments/${apartment.id}/cleaning-rates`, fetchOptions)
                    .then((response) => (response.ok ? response.json() : { cleaningRates: [] }))
                    .then((data: { cleaningRates?: CleaningRate[] }) => data.cleaningRates ?? [])
                    .catch(() => [] as CleaningRate[]),
            ),
        )
            .then((perApartment) => setRates(perApartment.flat()))
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch cleaning rates: ${error.message}`,
                    variant: "destructive",
                });
            });
    }, [activeApartments, reloadToken]);

    const filteredRates = useMemo(() => {
        if (predicates.length === 0) {
            return rates;
        }
        return rates.filter((rate) => {
            const apartment = apartmentEntities[rate.apartmentId];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [rates, predicates, apartmentEntities]);

    const editRate = useCallback((rate: CleaningRate) => {
        setAddApartmentId(undefined);
        setRateToEdit(rate);
        setShowEditModal(true);
    }, []);

    const handleAddRate = useCallback((apartmentId: number) => {
        setRateToEdit(undefined);
        setAddApartmentId(apartmentId);
        setShowEditModal(true);
    }, []);

    // The apartment the dialog operates on — either the row being edited or, in
    // add-mode, the apartment chosen from the missing-rates banner.
    const dialogApartmentId = rateToEdit?.apartmentId ?? addApartmentId ?? 0;

    const handleDelete = useCallback(
        async (rate: CleaningRate) => {
            if (!rate.id) return;
            try {
                const result = await removeCleaningRate({ apartmentId: rate.apartmentId, id: rate.id }).unwrap();
                successPopup("Cleaning rate removed successfully");
                if (result.affectedHistoricalPeriods) {
                    toast({
                        // Polite (background) announcement: this is an informational
                        // confirmation, not an error/warning that should interrupt the
                        // screen reader (WCAG 4.1.3). The destructive error toast above
                        // stays assertive (foreground).
                        type: "background",
                        title: "Historical incomes recomputed",
                        description:
                            "This change touched past-dated reservations — their incomes have been recomputed.",
                    });
                }
                reloadRates();
            } catch (error) {
                void errorPopup("Failed to remove cleaning rate: " + JSON.stringify(error));
            }
        },
        [removeCleaningRate, reloadRates],
    );

    const ratesForEditApartment = useMemo(
        () => rates.filter((rate) => rate.apartmentId === dialogApartmentId),
        [rates, dialogApartmentId],
    );

    const columnDefinitions: ColDef[] = buildCleaningRateColumnDefinitions({
        apartmentEntities,
        onEdit: editRate,
        onDelete: handleDelete,
    });

    const handleModalSubmitted = useCallback(
        async (cleaningFee: number, cleaningCost: number, effectiveFrom: Date, existingRate?: CleaningRate) => {
            const apartmentId = dialogApartmentId;
            if (!apartmentId) return;
            const isUpdate = !!existingRate;
            const payload: CleaningRate = {
                id: existingRate?.id,
                apartmentId,
                effectiveFrom: formatDateToISODate(effectiveFrom),
                cleaningFee,
                cleaningCost,
            };

            try {
                const result = isUpdate
                    ? await updateCleaningRate(payload).unwrap()
                    : await addCleaningRate(payload).unwrap();
                successPopup(isUpdate ? "Cleaning rate updated successfully" : "Cleaning rate created successfully");
                if (result.affectedHistoricalPeriods) {
                    toast({
                        // Polite (background) announcement: this is an informational
                        // confirmation, not an error/warning that should interrupt the
                        // screen reader (WCAG 4.1.3). The destructive error toast above
                        // stays assertive (foreground).
                        type: "background",
                        title: "Historical incomes recomputed",
                        description:
                            "This change touched past-dated reservations — their incomes have been recomputed.",
                    });
                }
                setShowEditModal(false);
                reloadRates();
            } catch (error) {
                void errorPopup("Failed to save cleaning rate: " + JSON.stringify(error));
            }
        },
        [dialogApartmentId, addCleaningRate, updateCleaningRate, reloadRates],
    );

    const handleModalClosed = useCallback(() => {
        setRateToEdit(undefined);
        setAddApartmentId(undefined);
        setShowEditModal(false);
    }, []);

    const missingApartments = useMemo(
        () =>
            readinessIds
                .map((id) => ({ id, name: apartmentEntities[id]?.name }))
                .filter((apartment): apartment is { id: number; name: string } => Boolean(apartment.name)),
        [readinessIds, apartmentEntities],
    );

    return (
        <div data-testid="cleaning-rates-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <MissingCleaningRatesBanner apartments={missingApartments} onAdd={handleAddRate} />
            <EditCleaningRateDialog
                open={showEditModal}
                onClose={handleModalClosed}
                apartmentName={apartmentEntities[dialogApartmentId]?.name || ""}
                apartmentId={dialogApartmentId}
                currentRate={rateToEdit}
                existingRates={ratesForEditApartment}
                onSubmit={handleModalSubmitted}
            />
            <Grid<CleaningRate>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={filteredRates}
                canExport={false}
                getRowId={(params) => `${params.data.apartmentId}-${params.data.id}`}
            />
        </div>
    );
};

export default CleaningRatesPage;
